import { Component } from '@opten/compiler/metadata/component';
import { addClass } from '@opten/dom/add-class';
import { hasClass } from '@opten/dom/has-class';
import { removeClass } from '@opten/dom/remove-class';
import { debounce } from '@opten/reactive/debounce';
import { isDesktop, isMobile, isPad } from '../utilities/detection';

declare var ScrollMagic: any;
declare var TweenMax: any;
declare var Linear: any;
declare var Power3: any;

export const ScrollMagicController = new ScrollMagic.Controller();

@Component({ className: 'js-magic-scroll' })
export class MagicScrollComponent {
	constructor(private triggerElement: HTMLElement) {
		const scene = this.triggerElement.getAttribute('data-magic-scene');
		if (scene != null) {
			switch (scene.toUpperCase()) {
				case 'STICKYCOLUMN':
					this.initSceneStickyColumn();
					break;
				case 'TANGRAM':
					this.initSceneTangram();
					break;
				default:
					break;
			}
		}
	}

	private initSceneTangram() {
		const header = document.getElementsByClassName(
			'js-header'
		)[0] as HTMLElement;

		const tangramWrapper = this.triggerElement.querySelector<HTMLElement>(
			'.js-magic-scroll-tangram-wrapper'
		);

		const tangramContainer = this.triggerElement.querySelector<HTMLElement>(
			'.js-magic-scroll-tangram-container'
		);

		const tangramContent = this.triggerElement.querySelector<HTMLElement>(
			'.js-magic-scroll-tangram-content'
		);
		const tangramElement = this.triggerElement.querySelector<HTMLElement>(
			'.js-magic-scroll-tangram'
		);

		const tangramSlide =
			this.triggerElement.querySelector<HTMLElement>(
				'.js-tangram__slide'
			);

		let scenes: any[] = [];

		if (isMobile() === false) {
			scenes = this.setSceneTangram(
				header,
				tangramWrapper,
				tangramContainer,
				tangramContent,
				tangramElement,
				tangramSlide
			);
		}
		window.addEventListener(
			'resize',
			debounce(() => {
				scenes.forEach(o => o.destroy(true));

				if (isMobile() === false) {
					scenes = this.setSceneTangram(
						header,
						tangramWrapper,
						tangramContainer,
						tangramContent,
						tangramElement,
						tangramSlide
					);
				}
			}, 100)
		);
	}

	private setSceneTangram(
		header: HTMLElement,
		tangramWrapper: HTMLElement,
		tangramContainer: HTMLElement,
		tangramContent: HTMLElement,
		tangramElement: HTMLElement,
		tangramSlide: HTMLElement
	) {
		const scenes: any[] = [];

		const headerHeight = getScrollMagicHeaderHeight(header);
		const spacingTopHeight = headerHeight;
		const durationContainer = spacingTopHeight;
		const fullHeightContainer =
			tangramContainer.getBoundingClientRect().height;
		const isAnimatingClassToToggle = 'is--animating';

		const triggerHook = headerHeight / window.innerHeight;

		const mainMagicScroll = document.querySelector<HTMLElement>(
			'.js-main-magic-scroll'
		);
		if (mainMagicScroll) {
			const mainMagicScrollScene = new ScrollMagic.Scene({
				triggerElement: this.triggerElement,
				triggerHook: triggerHook,
				duration: durationContainer
			})
				.setPin(mainMagicScroll)

				// .addIndicators({
				// 	name: "main magic container",
				// 	colorStart: 'orange',
				// 	colorEnd: 'orange',
				// 	colorTrigger: 'orange',
				// })
				.addTo(ScrollMagicController);
			scenes.push(mainMagicScrollScene);
		}

		const tangramContainerScene = new ScrollMagic.Scene({
			triggerElement: this.triggerElement,
			triggerHook: triggerHook,
			offset: 5
		})
			.setClassToggle(this.triggerElement, isAnimatingClassToToggle)
			// .addIndicators({
			// 	name: "main magic container",
			// 	colorStart: 'orange',
			// 	colorEnd: 'orange',
			// 	colorTrigger: 'orange',
			// })
			.addTo(ScrollMagicController);
		scenes.push(tangramContainerScene);

		const tangramWrapperStickyScene = new ScrollMagic.Scene({
			triggerElement: this.triggerElement,
			triggerHook: triggerHook,
			duration: durationContainer
		})
			.setPin(tangramWrapper, {
				pushFollowers: false
			})
			// .addIndicators({
			// 	name: "tangram container",
			// 	colorStart: 'red',
			// 	colorEnd: 'red',
			// 	colorTrigger: 'red',
			// })
			.addTo(ScrollMagicController);
		scenes.push(tangramWrapperStickyScene);
		if (tangramElement) {
			let containerWidthPart = 0;
			if (tangramContent) {
				containerWidthPart =
					tangramContent
						?.querySelector<HTMLElement>('.container')
						?.getBoundingClientRect().width ?? 0;
			}
			const targetSpacingLeft =
				containerWidthPart > 0
					? (window.innerWidth - containerWidthPart) / 2 +
					  (containerWidthPart / 12) * 3
					: (window.innerWidth / 100) * 26.6;
			const scalingSize =
				fullHeightContainer / (fullHeightContainer - 126);
			scenes.push(
				new ScrollMagic.Scene({
					triggerElement: this.triggerElement,
					triggerHook: triggerHook,
					offset: 5
				})
					.setTween(
						TweenMax.fromTo(
							tangramElement,
							1,
							{
								css: { x: 0, scaleX: 1, scaleY: 1 }
							},
							{
								css: {
									x: targetSpacingLeft,
									scaleX: scalingSize,
									scaleY: scalingSize
								},
								ease: Linear.ease
							}
						)
							.duration(0.1)
							.delay(0)
					)
					// .addIndicators({
					// 	name: "tangram",
					// 	colorStart: 'yellow',
					// 	colorEnd: 'yellow',
					// 	colorTrigger: 'yellow',
					// })
					.addTo(ScrollMagicController)
			);
		}
		if (tangramSlide) {
			const tangramSlideContents =
				tangramSlide.querySelectorAll<HTMLElement>(
					'.c-tangram__slide__content'
				);
			tangramSlideContents.forEach(tangramSlideContent => {
				if (tangramSlideContent) {
					if (hasClass(tangramSlide, isAnimatingClassToToggle)) {
						removeClass(tangramSlide, isAnimatingClassToToggle);
					}
					const originalClipPath = tangramSlideContent.getAttribute(
						'data-magic-original'
					);
					const targetClipPath =
						tangramSlideContent.getAttribute('data-magic-target');
					if (originalClipPath && targetClipPath) {
						tangramSlideContent.style.clipPath = originalClipPath;

						const proxySlideContent = {
							clipPath: originalClipPath
						};
						const tangramSlideContentScene = new ScrollMagic.Scene({
							triggerElement: this.triggerElement,
							triggerHook: triggerHook,
							offset: 5
						})
							.setTween(
								TweenMax.fromTo(
									proxySlideContent,
									1,
									{
										clipPath: originalClipPath
									},
									{
										clipPath: targetClipPath,
										onUpdate: () => {
											tangramSlideContent.style.clipPath =
												proxySlideContent.clipPath;
										}
									}
								)
									.duration(0.2)
									.delay(0.5)
							)
							// .addIndicators({
							// 	name: "tangramSlideContent",
							// 	colorStart: 'yellow',
							// 	colorEnd: 'yellow',
							// 	colorTrigger: 'yellow',
							// })
							.addTo(ScrollMagicController);
						scenes.push(tangramSlideContentScene);
					}
					const tangramSlideScene = new ScrollMagic.Scene({
						triggerElement: this.triggerElement,
						triggerHook: 0,
						//offset: spacingTopHeight,
						duration: durationContainer
					})
						.setClassToggle(tangramSlide, isAnimatingClassToToggle)
						.addTo(ScrollMagicController);
					tangramSlideScene.on('end', event => {
						if (event.scrollDirection !== 'REVERSE') {
							tangramSlideScene.removeClassToggle();
						} else {
							tangramSlideScene.setClassToggle(
								tangramSlide,
								isAnimatingClassToToggle
							);
						}
					});
					scenes.push(tangramSlideScene);
				}
			});
		}
		return scenes;
	}

	private initSceneStickyColumn() {
		const targetElements = Array.from(
			this.triggerElement.querySelectorAll<HTMLElement>(
				'.js-magic-scroll-target'
			)
		);
		let stickyColumnScene: any = null;

		if (isMobile() === false) {
			stickyColumnScene = this.setSceneStickyColumn(targetElements);
		}
		window.addEventListener(
			'resize',
			debounce(() => {
				if (stickyColumnScene) {
					stickyColumnScene.destroy(true);
				}

				for (const element of targetElements) {
					element.parentElement.removeAttribute('style');
				}
				if (isMobile() === false) {
					stickyColumnScene =
						this.setSceneStickyColumn(targetElements);
				}
			}, 100)
		);
	}

	private setSceneStickyColumn(targetElements: HTMLElement[]) {
		let smallerElement: HTMLElement;
		let smallestHeight = 5000;
		for (const element of targetElements) {
			const bounds = element.getBoundingClientRect();
			if (bounds.height < smallestHeight) {
				smallestHeight = bounds.height;
				smallerElement = element;
			}
		}
		const triggerBounds = this.triggerElement.getBoundingClientRect();
		const style = getComputedStyle(smallerElement.firstElementChild);
		const height =
			smallerElement.offsetHeight +
			parseInt(style.marginTop, 10) +
			parseInt(style.marginBottom, 10);

		const header = document.getElementsByClassName(
			'js-header'
		)[0] as HTMLElement;
		let headerHeight = getScrollMagicHeaderHeight(header);
		headerHeight += isPad() ? 30 : 0;

		let scene = new ScrollMagic.Scene({
			triggerElement: this.triggerElement,
			offset: -headerHeight,
			triggerHook: 0,
			duration: triggerBounds.height - height
		})
			.setPin(smallerElement)
			.addTo(ScrollMagicController);
		return scene;
	}
}

export function getScrollMagicHeaderHeight(headerElement: HTMLElement) {
	let headerHeight = Math.round(headerElement.getBoundingClientRect().height);
	headerHeight += hasClass(headerElement, '--base-only')
		? isDesktop()
			? 54
			: 99
		: 0;
	return headerHeight;
}
